import { useContext, useId, type ReactNode } from 'react';
import { AArrowDown, AArrowUp } from 'lucide-react';

import config from '@/config';
import { AppContext } from '@/app.context';
import { track } from '@shared/events';
import { languageName } from '@shared/lib/languages';
import { PSEUDO_LANGUAGE, useLanguage, useT, type MessageKey } from '@shared/i18n';
import {
	preferredLanguageOptions,
	resolvePreferredLanguage,
} from '@shared/lib/preferred-language';
import {
	DEFAULT_FONT_SIZE,
	LINE_HEIGHT_OPTIONS,
	MAX_FONT_SIZE,
	MIN_FONT_SIZE,
	FONT_SIZE_STEP,
	useFont,
	useLineHeight,
	useMotionPreference,
	type ColorSchemePreference,
	type MotionPreference,
} from '@shared/lib/hooks';

import {
	PLAYBACK_RATES,
	analyticsPreference,
	readerStatsPreference,
	articleAlignPreference,
	articleFontPreference,
	darkArticlesPreference,
	pageLayoutPreference,
	playbackRatePreference,
	readingModePreference,
	underlineLinksPreference,
	type ArticleFont,
} from '@shared/lib/reader-preferences';

import { SETTING_PANELS } from './setting-panels';
import { STATS_PANELS } from './stats-panels';
import styles from './settings-dialog.module.css';

/**
 * The ids of the row's visible label and description, which name and
 * describe the control (`aria-labelledby`/`aria-describedby`) instead of a
 * second, hidden copy of the same words.
 */
export interface SettingControlProps {
	labelId: string;
	descriptionId?: string;
}

/**
 * One control per setting id in `model/settings-registry.ts`. Each reads
 * and writes the same store the rest of the app uses, so a change applies
 * immediately everywhere -- there's no Save button, same as Claude's.
 */
export const SETTING_CONTROLS: Record<
	string,
	(props: SettingControlProps) => ReactNode
> = {
	theme: ThemeControl,
	motion: MotionControl,
	language: LanguageControl,
	'text-size': TextSizeControl,
	'line-spacing': LineSpacingControl,
	'article-font': ArticleFontControl,
	'text-align': TextAlignControl,
	'reading-mode': ReadingModeControl,
	'page-layout': PageLayoutControl,
	'dark-articles': DarkArticlesControl,
	'playback-speed': PlaybackSpeedControl,
	'underline-links': UnderlineLinksControl,
	analytics: AnalyticsControl,
	'reading-stats': ReadingStatsControl,
	...SETTING_PANELS,
	...STATS_PANELS,
};

/**
 * Wraps a setting's setter so the change is also recorded
 * (`setting_change`). `setting` is the setting's id in the registry.
 */
function recorded<T>(setting: string, set: (value: T) => void): (value: T) => void {
	return (value) => {
		set(value);
		track('setting_change', { setting, value: String(value) });
	};
}

const THEME_OPTIONS: ReadonlyArray<{
	value: ColorSchemePreference;
	label: MessageKey;
}> = [
	{ value: 'light', label: 'settings.theme.light' },
	{ value: 'system', label: 'settings.theme.auto' },
	{ value: 'dark', label: 'settings.theme.dark' },
];

/**
 * Preview cards, like Claude's "Color mode": a sketch of the app in each
 * mode. The sketches use fixed colours on purpose -- they depict light and
 * dark, whichever mode is showing now.
 */
function ThemeControl({ labelId, descriptionId }: SettingControlProps) {
	const { colorSchemePreference, setColorSchemePreference } =
		useContext(AppContext);
	const name = useId();
	const t = useT();
	return (
		<div
			role="radiogroup"
			aria-labelledby={labelId}
			aria-describedby={descriptionId}
			className={styles['theme-options']}
		>
			{THEME_OPTIONS.map((option) => (
				<label key={option.value} className={styles['theme-option']}>
					<input
						type="radio"
						name={name}
						value={option.value}
						checked={colorSchemePreference === option.value}
						onChange={() =>
							recorded('theme', setColorSchemePreference)(option.value)
						}
					/>
					<span
						className={styles['theme-preview']}
						data-preview={option.value}
						aria-hidden="true"
					>
						<span />
						<span />
						<span />
					</span>
					<span>{t(option.label)}</span>
				</label>
			))}
		</div>
	);
}

const MOTION_OPTIONS: ReadonlyArray<{
	value: MotionPreference;
	label: MessageKey;
}> = [
	{ value: 'system', label: 'settings.motion.system' },
	{ value: 'reduce', label: 'settings.motion.reduced' },
	{ value: 'full', label: 'settings.motion.full' },
];

function MotionControl(props: SettingControlProps) {
	const { motionPreference, setMotionPreference } = useMotionPreference();
	return (
		<Segmented
			{...props}
			options={MOTION_OPTIONS}
			value={motionPreference}
			onChange={recorded('motion', setMotionPreference)}
		/>
	);
}

const LINE_HEIGHT_LABELS: Record<
	(typeof LINE_HEIGHT_OPTIONS)[number]['value'],
	MessageKey
> = {
	1: 'settings.lineSpacing.normal',
	1.25: 'settings.lineSpacing.relaxed',
	1.5: 'settings.lineSpacing.loose',
};

function LineSpacingControl(props: SettingControlProps) {
	const { lineHeight, setLineHeight } = useLineHeight();
	return (
		<Segmented
			{...props}
			options={LINE_HEIGHT_OPTIONS.map(({ value }) => ({
				value,
				label: LINE_HEIGHT_LABELS[value],
			}))}
			value={lineHeight}
			onChange={recorded('line-spacing', setLineHeight)}
		/>
	);
}

/**
 * Radio inputs styled as a segmented control: arrow keys, one tab stop and
 * the checked state come from the browser. Labels are message keys, or
 * `text` for ones that aren't words (playback rates).
 */
function Segmented<T extends string | number>({
	labelId,
	descriptionId,
	options,
	value,
	onChange,
}: SettingControlProps & {
	options: ReadonlyArray<{ value: T; label: MessageKey } | { value: T; text: string }>;
	value: T;
	onChange: (value: T) => void;
}) {
	const name = useId();
	const t = useT();
	return (
		<div
			role="radiogroup"
			aria-labelledby={labelId}
			aria-describedby={descriptionId}
			className={styles['segmented']}
		>
			{options.map((option) => (
				<label key={String(option.value)}>
					<input
						type="radio"
						name={name}
						value={String(option.value)}
						checked={value === option.value}
						onChange={() => onChange(option.value)}
					/>
					<span>{'label' in option ? t(option.label) : option.text}</span>
				</label>
			))}
		</div>
	);
}

/**
 * Same steps and bounds as the reader menu's A↓/A↑ buttons -- both call
 * the one `useFont` store -- shown as a percentage of the default.
 */
function TextSizeControl({ labelId, descriptionId }: SettingControlProps) {
	const { fontSize, setFontSize } = useFont();
	const t = useT();
	const percent = Math.round((fontSize / DEFAULT_FONT_SIZE) * 100);
	// Recorded as the percentage the reader sees, not the pixel size.
	const set = (size: number) => {
		setFontSize(size);
		track('setting_change', {
			setting: 'text-size',
			value: String(Math.round((size / DEFAULT_FONT_SIZE) * 100)),
		});
	};
	return (
		<fieldset
			className={styles['stepper']}
			aria-labelledby={labelId}
			aria-describedby={descriptionId}
		>
			<button
				type="button"
				aria-label={t('settings.textSize.decrease')}
				disabled={fontSize <= MIN_FONT_SIZE}
				onClick={() => set(fontSize - FONT_SIZE_STEP)}
			>
				<AArrowDown aria-hidden="true" />
			</button>
			<output aria-live="polite">{percent}%</output>
			<button
				type="button"
				aria-label={t('settings.textSize.increase')}
				disabled={fontSize >= MAX_FONT_SIZE}
				onClick={() => set(fontSize + FONT_SIZE_STEP)}
			>
				<AArrowUp aria-hidden="true" />
			</button>
			<button
				type="button"
				className={styles['reset']}
				disabled={fontSize === DEFAULT_FONT_SIZE}
				onClick={() => set(DEFAULT_FONT_SIZE)}
			>
				{t('settings.textSize.reset')}
			</button>
		</fieldset>
	);
}

/**
 * The preferred (app-wide) language: the tenant's `languages`, starting on
 * its `defaultLanguage`. Names are shown in the current language, the way
 * the reader's own language menu shows them.
 */
function LanguageControl({ labelId, descriptionId }: SettingControlProps) {
	const { lang, onLanguageChange } = useContext(AppContext);
	const { language: defaultLanguage, languages: configured } = config.build;
	const languages = preferredLanguageOptions(defaultLanguage, configured);
	// Dev only: the pseudo-locale (docs/wiki/I18n.md#checking-your-work).
	if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
		languages.push(PSEUDO_LANGUAGE);
	}
	const uiLanguage = useLanguage();
	return (
		<select
			className={styles['select']}
			aria-labelledby={labelId}
			aria-describedby={descriptionId}
			value={
				uiLanguage === PSEUDO_LANGUAGE
					? PSEUDO_LANGUAGE
					: resolvePreferredLanguage(lang, defaultLanguage, configured)
			}
			onChange={(event) => {
				const next = event.target.value;
				onLanguageChange(next);
				track('language_change', { to: next, scope: 'app' });
			}}
		>
			{languages.map((code) => (
				<option key={code} value={code}>
					{code === PSEUDO_LANGUAGE
						? 'Pseudo (en-XA)'
						: languageName(code, lang)}
				</option>
			))}
		</select>
	);
}

const ARTICLE_FONT_OPTIONS: ReadonlyArray<{
	value: ArticleFont;
	label: MessageKey;
}> = [
	{ value: 'publisher', label: 'settings.articleFont.publisher' },
	{ value: 'sans', label: 'settings.articleFont.sans' },
	{ value: 'serif', label: 'settings.articleFont.serif' },
];

function ArticleFontControl(props: SettingControlProps) {
	const [font, setFont] = articleFontPreference.use();
	return (
		<Segmented
			{...props}
			options={ARTICLE_FONT_OPTIONS}
			value={font}
			onChange={recorded('article-font', setFont)}
		/>
	);
}

function TextAlignControl(props: SettingControlProps) {
	const [align, setAlign] = articleAlignPreference.use();
	return (
		<Segmented
			{...props}
			options={[
				{ value: 'publisher', label: 'settings.textAlign.keep' },
				{ value: 'start', label: 'settings.textAlign.start' },
			]}
			value={align}
			onChange={recorded('text-align', setAlign)}
		/>
	);
}

function ReadingModeControl(props: SettingControlProps) {
	const [mode, setMode] = readingModePreference.use();
	return (
		<Segmented
			{...props}
			options={[
				{ value: 'responsive', label: 'settings.readingMode.text' },
				{ value: 'replica', label: 'settings.readingMode.page' },
			]}
			value={mode}
			onChange={recorded('reading-mode', setMode)}
		/>
	);
}

function PageLayoutControl(props: SettingControlProps) {
	const [layout, setLayout] = pageLayoutPreference.use();
	return (
		<Segmented
			{...props}
			options={[
				{ value: 'single', label: 'settings.pageLayout.single' },
				{ value: 'double', label: 'settings.pageLayout.double' },
			]}
			value={layout}
			onChange={recorded('page-layout', setLayout)}
		/>
	);
}

function PlaybackSpeedControl(props: SettingControlProps) {
	const [rate, setRate] = playbackRatePreference.use();
	// 1,5× in languages with a decimal comma.
	const language = useLanguage();
	const number = new Intl.NumberFormat(
		language === PSEUDO_LANGUAGE ? 'en' : language,
	);
	return (
		<Segmented
			{...props}
			options={PLAYBACK_RATES.map((value) => ({
				value,
				text: `${number.format(value)}×`,
			}))}
			value={rate}
			onChange={recorded('playback-speed', setRate)}
		/>
	);
}

function DarkArticlesControl(props: SettingControlProps) {
	const [value, setValue] = darkArticlesPreference.use();
	return (
		<Switch
			{...props}
			checked={value === 'on'}
			onChange={(checked) =>
				recorded('dark-articles', setValue)(checked ? 'on' : 'off')
			}
		/>
	);
}

function UnderlineLinksControl(props: SettingControlProps) {
	const [value, setValue] = underlineLinksPreference.use();
	return (
		<Switch
			{...props}
			checked={value === 'on'}
			onChange={(checked) =>
				recorded('underline-links', setValue)(checked ? 'on' : 'off')
			}
		/>
	);
}

/**
 * The reader's own figures, on this device only. Independent of the
 * Analytics switch above it: neither turns the other on or off.
 */
function ReadingStatsControl(props: SettingControlProps) {
	const [value, setValue] = readerStatsPreference.use();
	return (
		<Switch
			{...props}
			checked={value === 'on'}
			onChange={(checked) =>
				recorded('reading-stats', setValue)(checked ? 'on' : 'off')
			}
		/>
	);
}

function AnalyticsControl(props: SettingControlProps) {
	const [value, setValue] = analyticsPreference.use();
	return (
		<Switch
			{...props}
			checked={value === 'on'}
			onChange={(checked) => setValue(checked ? 'on' : 'off')}
		/>
	);
}

/** An on/off switch: a button with `role="switch"`, named by its row. */
function Switch({
	labelId,
	descriptionId,
	checked,
	onChange,
}: SettingControlProps & {
	checked: boolean;
	onChange: (checked: boolean) => void;
}) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-labelledby={labelId}
			aria-describedby={descriptionId}
			className={styles['switch']}
			onClick={() => onChange(!checked)}
		>
			<span aria-hidden="true" />
		</button>
	);
}
